import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { getExtensionValue, resolveId } from "@medplum/core";
import { CodeableConcept, Coverage } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { ActionIcon, Avatar, Badge, Card, Divider, Group, Menu, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconDotsVertical, IconFileExport } from "@tabler/icons-react";

import ClientExportSelectSectionDrawer from "@/client-export/components/ClientExportSelectSectionDrawer";
import { getInsuranceInformation } from "@/clients/services/insurance-information";
import { formatClientAge } from "@/clients/utils/client-age";
import {
  getPaymentMethodFromIntake,
  getPrimaryInsuranceCompanyFromIntake,
} from "@/clients/utils/intake-primary-insurance-company";
import { getPatientMrn } from "@/clients/utils/mrn";
import { isLaborEncounter, isPatientInLabor } from "@/clients/utils/patient-labor";
import { TextWrapper } from "@/components/TextWrapper";
import { PROFILE_AVATAR_SIZE } from "@/constants/sizes";
import { PartnerContext } from "@/contexts/partner";
import { PatientContext } from "@/contexts/patient";
import { Extensions } from "@/fhir/enums/extensions";
import { LOINCAnswerCodes } from "@/fhir/enums/loinc";
import { requiresInterpreter } from "@/fhir/patient-communication";
import { usePracticeId } from "@/hooks/usePracticeId";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { useAppDispatch } from "@/store";
import {
  selectInsuranceLastUpdated,
  selectQuestionnaireResponse,
} from "@/store/slices/client-billing";
import { selectIsInLabor, setInLabor } from "@/store/slices/inLabor";
import { patientHasUser } from "@/utils/check-patient-user";
import { getDisplayStringAsync } from "@/utils/medplum";
import {
  getAllNames,
  getDisplayName,
  getInitialsFromName,
  getNameAriaLabel,
  getProfilePicture,
} from "@/utils/person";
import { formatPhoneNumber } from "@/utils/str";
import { capitalize, capitalizeWords, formatDateMDY } from "@/utils/strings";

import classes from "./ClientInformationSummary.module.css";
import ClientInLaborCard from "./ClientInLaborCard";
import ClientInterpreterCard from "./ClientInterpreterCard";

// TODO: Replace hardcoded values with external data.

const MEDICAID_NAME = "Medicaid";
const NO_PARTNERS_NAME = "No partner";

export default function ClientInformationSummary() {
  const { patient: client } = useContext(PatientContext);
  const { partners } = useContext(PartnerContext);
  const { hasChartExportPermission } = usePractitionerPermissions();
  const medplum = useMedplum();
  const { encounterId } = useParams() as { encounterId?: string };
  const effectiveEncounterId = encounterId && encounterId !== "new" ? encounterId : undefined;
  const dispatch = useAppDispatch();
  const isInLabor = useSelector(selectIsInLabor);
  const billingQuestionnaireResponse = useSelector(selectQuestionnaireResponse);
  const insuranceLastUpdated = useSelector(selectInsuranceLastUpdated);
  const [
    isClientExportViewerOpened,
    { open: openClientExportViewer, close: closeClientExportViewer },
  ] = useDisclosure(false);

  const practiceIdFromHooks = usePracticeId();
  const managingOrganizationId = useMemo(
    () => practiceIdFromHooks || resolveId(client?.managingOrganization),
    [client, practiceIdFromHooks],
  );

  const { firstName, lastName } = getAllNames(client);
  const dob = client?.birthDate;
  const formattedDob = formatDateMDY(dob);
  const age = formatClientAge(dob);
  const [insuranceInformation, setInsuranceInformation] = useState<{
    coverage: Coverage | undefined;
    medicaidResponseCode: string;
    paymentMethod: string;
  }>({ coverage: undefined, medicaidResponseCode: "", paymentMethod: "" });
  const [payorDisplayName, setPayorDisplayName] = useState<string>("");

  /** Global billing slice can hold another patient's QuestionnaireResponse until overwritten; only derive intake label when the subject matches the profile patient. */
  const billingQuestionnaireResponseForPatient = useMemo(() => {
    if (!client?.id || !billingQuestionnaireResponse) return undefined;
    if (resolveId(billingQuestionnaireResponse.subject) !== client.id) return undefined;
    return billingQuestionnaireResponse;
  }, [billingQuestionnaireResponse, client?.id]);

  const intakePrimaryInsuranceCompany = useMemo(
    () => getPrimaryInsuranceCompanyFromIntake(billingQuestionnaireResponseForPatient),
    [billingQuestionnaireResponseForPatient],
  );
  const intakePaymentMethod = useMemo(
    () => getPaymentMethodFromIntake(billingQuestionnaireResponseForPatient),
    [billingQuestionnaireResponseForPatient],
  );
  const insuranceCoverage = insuranceInformation.coverage;

  const paymentMethodDisplay = intakePaymentMethod.trim() || insuranceInformation.paymentMethod;

  useEffect(() => {
    const patientId = client?.id;
    if (!patientId) return;

    let cancelled = false;

    void (async () => {
      try {
        await Promise.all([
          medplum.invalidateSearches("Coverage"),
          medplum.invalidateSearches("Observation"),
        ]);
        if (cancelled) return;
        const result = await getInsuranceInformation(medplum, patientId);
        if (cancelled) return;
        setInsuranceInformation(result);
      } catch (error) {
        if (!cancelled) console.warn("Failed to fetch insurance information:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    medplum,
    client?.id,
    insuranceLastUpdated,
    billingQuestionnaireResponseForPatient?.meta?.lastUpdated,
  ]);

  useEffect(() => {
    const fromIntake = intakePrimaryInsuranceCompany.trim();
    const payorReference = insuranceCoverage?.payor?.[0];
    const hasResolvablePayor = Boolean(insuranceCoverage && payorReference?.reference);

    if (!hasResolvablePayor) {
      setPayorDisplayName(fromIntake);
      return;
    }

    const firstPayorEntry = payorReference!;
    const payorReferenceString = firstPayorEntry.reference!;
    if (!payorReferenceString.includes("/")) {
      setPayorDisplayName(payorReferenceString);
      return;
    }

    let cancelled = false;

    void (async () => {
      const fullPayorReference = { ...firstPayorEntry, reference: payorReferenceString };
      try {
        const displayName = await getDisplayStringAsync(
          medplum,
          fullPayorReference,
          firstPayorEntry.display?.trim() || fromIntake,
        );
        if (!cancelled) setPayorDisplayName(displayName as string);
      } catch (error) {
        if (cancelled) return;
        const fallback = firstPayorEntry.display?.trim() || fromIntake || "";
        setPayorDisplayName(fallback);
        console.warn("Failed to resolve coverage payor display:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [intakePrimaryInsuranceCompany, medplum, insuranceCoverage]);

  // Get initials from first given name and family name (not middle name)
  const initials = getInitialsFromName(client);
  const languages =
    client?.communication?.map((c) => c.language?.coding?.[0]?.display).join(", ") || "";
  const interpreterRequired = requiresInterpreter(client?.communication);
  const fullPronouns = (getExtensionValue(client, Extensions.IndividualPronouns) as CodeableConcept)
    ?.coding?.[0]?.display;
  // Trim pronouns to first two parts (e.g., "she/her/her/hers/herself" -> "she/her")
  const pronouns = fullPronouns?.split("/").slice(0, 2).join("/");
  const partnerNames = useMemo(() => {
    if (partners.length === 0) return NO_PARTNERS_NAME;
    return partners
      .reduce<string[]>((names, partner) => {
        const { firstName, lastName } = getAllNames(partner);
        if (firstName === "Name Unavailable" && lastName === "Unknown") return names;
        const name = `${capitalize(firstName)} ${capitalize(lastName)}`.trim();
        const fullPronouns = (
          getExtensionValue(partner, Extensions.IndividualPronouns) as CodeableConcept
        )?.coding?.[0]?.display;
        // Trim pronouns to first two parts (e.g., "she/her/her/hers/herself" -> "she/her")
        const partnerPronouns = fullPronouns?.split("/").slice(0, 2).join("/");
        names.push(partnerPronouns ? `${name} (${partnerPronouns})` : name);
        return names;
      }, [])
      .join(", ");
  }, [partners]);
  const canCallByPhone = getExtensionValue(client, Extensions.CanCallToPreferredContact) as boolean;
  const contactPreferences = {
    Portal: client ? patientHasUser(client) : false,
    Phone: canCallByPhone,
    VM: getExtensionValue(client, Extensions.CanLeaveVoicemailToPreferredContact) as boolean,
  };

  // Get preferred phone numbers if contact by phone is allowed
  const phoneNumbers = useMemo(() => {
    if (!canCallByPhone || !client?.telecom) return [];
    return client.telecom
      .filter((t) => t.system === "phone" && t.value && t.rank === 1)
      .map((t) => ({
        number: formatPhoneNumber(t.value!.replace(/\D/g, "")),
        type: t.use ? capitalize(t.use) : "Phone",
      }));
  }, [canCallByPhone, client?.telecom]);

  // Get MRN using the proper utility function
  const mrn = client ? getPatientMrn(client) || "not available" : "not available";
  const profilePhoto = getProfilePicture(client);

  // Check if patient is in labor (driven by URL encounter when present, else any in-progress labor); updates Redux
  useEffect(() => {
    const clientId = client?.id;
    if (!clientId) return;

    if (!effectiveEncounterId) {
      isPatientInLabor(medplum, clientId).then((inLabor) => dispatch(setInLabor(inLabor)));
      return;
    }

    (async () => {
      try {
        const encounter = await medplum.readResource("Encounter", effectiveEncounterId);
        const [isLaborEncounterCode, isInProgress] = isLaborEncounter(encounter);
        if (isLaborEncounterCode && isInProgress) {
          dispatch(setInLabor(true));
        } else if (isLaborEncounterCode) {
          dispatch(setInLabor(false));
        } else {
          const inLabor = await isPatientInLabor(medplum, clientId);
          dispatch(setInLabor(inLabor));
        }
      } catch {
        const inLabor = await isPatientInLabor(medplum, clientId);
        dispatch(setInLabor(inLabor));
      }
    })();
  }, [medplum, dispatch, client?.id, effectiveEncounterId]);

  const handleOpenClientExportViewer = () => {
    openClientExportViewer();
  };

  return (
    <>
      <ClientExportSelectSectionDrawer
        clientId={client?.id ?? ""}
        onClose={closeClientExportViewer}
        opened={isClientExportViewerOpened}
        practiceId={managingOrganizationId}
      />

      <Card
        py={24}
        px={16}
        mx="auto"
        c="white"
        bg="white"
        radius="0"
        className={classes.infoSummaryCard}
        aria-label="Client Information Summary"
      >
        <Card.Section className={classes.headerSection}>
          <Group wrap="nowrap" align="flex-start">
            <Avatar
              bg="burgundy.1"
              src={profilePhoto}
              color="burgundy.9"
              aria-label="Client Avatar"
              size={PROFILE_AVATAR_SIZE}
              styles={{ placeholder: { color: "var(--mantine-color-burgundy-9)" } }}
            >
              {initials}
            </Avatar>
            <Stack gap="0" justify="start" style={{ flex: 1 }}>
              <Group wrap="nowrap" justify="space-between">
                <Group gap="xs" wrap="nowrap">
                  <TextWrapper size="xl" c={"dark.9"} variant="bold" aria-label="Client Full Name">
                    {capitalize(firstName)} {capitalize(lastName)}
                  </TextWrapper>
                  {pronouns && (
                    <TextWrapper
                      px={2}
                      py={2}
                      size="xs"
                      c="dark.3"
                      variant="label"
                      aria-label="Client Pronouns"
                      style={{ borderRadius: 4, whiteSpace: "nowrap" }}
                    >
                      {pronouns}
                    </TextWrapper>
                  )}
                </Group>
                {hasChartExportPermission && (
                  <Menu>
                    <Menu.Target>
                      <ActionIcon
                        color="gray.6"
                        variant="transparent"
                        data-testid="client-header-actions"
                      >
                        <IconDotsVertical size={24} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconFileExport />}
                        onClick={handleOpenClientExportViewer}
                        data-testid="client-header-export-chart"
                      >
                        Export Chart
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
              <Group pb="xs">
                <TextWrapper
                  size="xs"
                  c={"gray.6"}
                  variant="bold"
                  aria-label="Client Date of Birth"
                >
                  Preferred Name
                </TextWrapper>

                <TextWrapper
                  size="xs"
                  c={"dark.9"}
                  variant="bold"
                  aria-label={getNameAriaLabel(client)}
                >
                  {capitalizeWords(getDisplayName(client))}
                </TextWrapper>
              </Group>
              <Group gap="md">
                <Group gap={4}>
                  <TextWrapper
                    size="xs"
                    c={"gray.6"}
                    variant="bold"
                    aria-label="Client Date of Birth"
                  >
                    DOB
                  </TextWrapper>
                  <TextWrapper size="xs" c={"dark.9"} variant="bold" aria-label="Client Age">
                    {formattedDob} ({age})
                  </TextWrapper>
                </Group>
                <Group gap={4}>
                  <TextWrapper
                    size="xs"
                    c={"gray.6"}
                    variant="bold"
                    aria-label="Client Medical Records Number"
                  >
                    MRN
                  </TextWrapper>
                  <TextWrapper
                    size="xs"
                    c={"dark.9"}
                    variant="bold"
                    aria-label="Client Medical Records Number"
                  >
                    {mrn}
                  </TextWrapper>
                </Group>
              </Group>
              <Group gap="xs">
                <ClientInLaborCard inLabor={isInLabor} />
                {paymentMethodDisplay ? (
                  <TextWrapper
                    my={4}
                    px={8}
                    py={2}
                    size="sm"
                    c="dark.3"
                    bg="gray.2"
                    variant="label"
                    style={{ borderRadius: 4 }}
                    aria-label="Client Payment Method"
                  >
                    {paymentMethodDisplay}
                  </TextWrapper>
                ) : null}
                {payorDisplayName ? (
                  <TextWrapper
                    my={4}
                    px={8}
                    py={2}
                    size="sm"
                    c="dark.3"
                    bg="gray.2"
                    variant="label"
                    style={{ borderRadius: 4 }}
                    aria-label="Client Primary Insurance"
                  >
                    {payorDisplayName}
                  </TextWrapper>
                ) : null}
                {insuranceInformation.medicaidResponseCode === LOINCAnswerCodes.Yes && (
                  <TextWrapper
                    my={4}
                    px={8}
                    py={2}
                    size="sm"
                    c="dark.3"
                    bg="gray.2"
                    variant="label"
                    style={{ borderRadius: 4 }}
                    aria-label="Client Medicaid"
                  >
                    {MEDICAID_NAME}
                  </TextWrapper>
                )}
              </Group>
            </Stack>
          </Group>
        </Card.Section>
        <Card.Section p={0}>
          <ClientInterpreterCard languages={languages} required={interpreterRequired} />
        </Card.Section>
        <Card.Section p={24}>
          <Group gap="4" pb="xs" aria-label="Client Partner">
            <TextWrapper size="xs" c={"dark.9"} style={{ whiteSpace: "nowrap" }}>
              Partner{partners.length > 1 ? "s" : ""}:{" "}
            </TextWrapper>
            <TextWrapper size="xs" c={"dark.9"} variant="bold">
              {partnerNames}
            </TextWrapper>
          </Group>

          {phoneNumbers.length > 0 && (
            <Group gap="4" pb="xs">
              <TextWrapper size="xs" c={"dark.9"} aria-label="Client Contact Information">
                Contact Information:
              </TextWrapper>
              {phoneNumbers.map((phone, index) => (
                <TextWrapper size="xs" key={index} c={"dark.9"} variant="bold">
                  {phone.number} ({phone.type}){index < phoneNumbers.length - 1 ? "," : ""}
                </TextWrapper>
              ))}
            </Group>
          )}
          <Group gap="4">
            <TextWrapper size="xs" c={"dark.9"} aria-label="Client Contact Preferences">
              Contact Preferences:
            </TextWrapper>
            {Object.entries(contactPreferences).map(([preference, enabled]) => (
              <Badge
                fz="xs"
                fw={500}
                size="xs"
                c="black"
                color="cream.4"
                key={preference}
                variant="outline"
              >
                <TextWrapper size="xs" variant="bold">
                  {enabled ? preference.toUpperCase() : `NO ${preference.toUpperCase()}`}
                </TextWrapper>
              </Badge>
            ))}
          </Group>
        </Card.Section>
      </Card>
      <Divider color="cream.3" />
    </>
  );
}
